import { WindowTitle } from "@dashboard/components/WindowTitle";
import {
  CartSnapshotSortField,
  OrderDirection,
  useCartSnapshotPromoteToDraftMutation,
  useCartSnapshotsQuery,
} from "@dashboard/graphql";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import usePaginator, {
  createPaginationState,
  PaginatorContext,
} from "@dashboard/hooks/usePaginator";
import { commonMessages } from "@dashboard/intl";
import { orderUrl } from "@dashboard/orders/urls";
import { ListViews } from "@dashboard/types";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { FC, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { CartVisibilityListSortField } from "../components/CartVisibilityListDatagrid/datagrid";
import { CartVisibilityListPage } from "../components/CartVisibilityListPage";
import { messages } from "../components/CartVisibilityListPage/messages";
import {
  cartVisibilityListUrl,
  CartVisibilityListUrlQueryParams,
  cartVisibilityUrl,
} from "../urls";

interface CartVisibilityListViewProps {
  params: CartVisibilityListUrlQueryParams;
}

const sortFieldMap: Record<CartVisibilityListSortField, CartSnapshotSortField> = {
  customer: CartSnapshotSortField.CUSTOMER,
  items: CartSnapshotSortField.ITEMS,
  total: CartSnapshotSortField.TOTAL,
  lastChange: CartSnapshotSortField.LAST_CHANGE,
};

export const CartVisibilityListView: FC<CartVisibilityListViewProps> = ({ params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const { updateListSettings, settings } = useListSettings(ListViews.CART_VISIBILITY_LIST);

  // Search + sort are held in local state and pushed to the backend `cartSnapshots`
  // query (server-side, like orders). Changing either resets pagination to page one.
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<{ sort: CartVisibilityListSortField; asc: boolean }>({
    sort: "lastChange",
    asc: false,
  });

  usePaginationReset(cartVisibilityListUrl, params, settings.rowNumber);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const queryVariables = useMemo(
    () => ({
      ...paginationState,
      filter: search ? { search } : {},
      sortBy: {
        field: sortFieldMap[sort.sort],
        direction: sort.asc ? OrderDirection.ASC : OrderDirection.DESC,
      },
    }),
    [paginationState, search, sort],
  );

  const { data, loading } = useCartSnapshotsQuery({
    displayLoader: true,
    variables: queryVariables,
  });

  const [promote] = useCartSnapshotPromoteToDraftMutation({
    onCompleted: result => {
      const payload = result.cartSnapshotPromoteToDraft;

      if (payload?.order && (payload.errors?.length ?? 0) === 0) {
        navigate(orderUrl(payload.order.id));
      } else {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong),
        });
      }
    },
  });

  const paginationValues = usePaginator({
    pageInfo: data?.cartSnapshots?.pageInfo,
    paginationState,
    queryString: params,
  });

  const carts = mapEdgesToItems(data?.cartSnapshots) ?? [];

  const resetPage = () => {
    if (params.after || params.before) {
      navigate(cartVisibilityListUrl());
    }
  };

  const handleSort = (field: CartVisibilityListSortField) => {
    resetPage();
    setSort(prev => ({ sort: field, asc: prev.sort === field ? !prev.asc : true }));
  };

  const handleSearchChange = (value: string) => {
    resetPage();
    setSearch(value);
  };

  return (
    <PaginatorContext.Provider value={paginationValues}>
      <WindowTitle title={intl.formatMessage(messages.title)} />
      <CartVisibilityListPage
        carts={carts}
        disabled={loading}
        settings={settings}
        sort={sort}
        onSort={handleSort}
        initialSearch={search}
        onSearchChange={handleSearchChange}
        onUpdateListSettings={updateListSettings}
        onRowClick={id => navigate(cartVisibilityUrl(id))}
        onTakeOver={token => promote({ variables: { token } })}
        onOpenDraft={id => navigate(orderUrl(id))}
      />
    </PaginatorContext.Provider>
  );
};

export default CartVisibilityListView;
